export default {
  nav: {
    coreLogic: 'Core Logic',
    profitMechanism: 'Profit Mechanism',
    profitFlywheel: 'Profit Flywheel',
    sysArch: 'System Architecture',
    tradingExample: 'Trading Cases',
    cta: 'Get Started',
    language: 'Language',
  },
  hero: {
    badge: 'Poly Ai Claw Intelligence Engine',
    title1: 'From Prediction to',
    title2: 'Probability Arbitrage',
    desc: 'Polywin upgrades prediction markets into a systematic arbitrage engine powered by the Poly Ai Claw Intelligence Engine',
    ctaPrimary: 'Launch Poly Ai Claw Engine',
    ctaSecondary: 'Watch Demo',
    statsEvents: 'Monitored Events',
    statsSuccessRate: 'Execution Success Rate',
    statsExecTime: 'Execution Time',
    cardTitle: 'Probability Deviation Analysis',
    cardBadge: 'EV Edge Zone',
    cardMarketProb: 'Market Probability',
    cardAiProb: 'AI Probability',
    cardMarketImplied: 'Market Implied Probability',
    cardAiTrue: 'AI True Probability',
    cardProbDiff: 'Probability Edge',
  },
  coreLogic: {
    badge: 'PolyAi-Claw Decision System',
    title1: 'From Data Aggregation to Compound Returns —',
    title2: 'Fully Automated Loop',
    desc: 'Multi-source data → AI modeling → Probability deviation → Auto execution → Compound returns: a complete automated trading loop',
    step0_title: 'Data Layer',
    step0_sub: 'Multi-source Aggregation',
    step0_i0: 'Political Events',
    step0_i1: 'Macro Economics',
    step0_i2: 'Social Sentiment',
    step0_i3: 'Order Book Data',
    step1_title: 'AI Modeling',
    step1_sub: 'Bayesian Probability Update',
    step1_i0: 'Real-time Data Input',
    step1_i1: 'AI Modeling Engine',
    step1_i2: 'Probability Calculation',
    step1_i3: 'Model Optimization',
    step2_title: 'Deviation Detection',
    step2_sub: 'Probability Deviation ID',
    step2_i0: 'Market Implied Probability',
    step2_i1: 'AI True Probability',
    step2_i2: 'EV Edge Detection',
    step2_i3: 'Position Signal',
    step3_title: 'On-chain Execution',
    step3_sub: 'Polygon Auto Trade',
    step3_i0: 'Risk Control',
    step3_i1: 'Capital Allocation',
    step3_i2: 'USDC Settlement',
    step3_i3: 'Smart Contract',
    step4_title: 'Compound Returns',
    step4_sub: 'Auto Reinvestment',
    step4_i0: 'Event Settlement',
    step4_i1: 'Profit Liquidation',
    step4_i2: 'Compound Calculation',
    step4_i3: 'Reinvestment Loop',
    keyTitle: 'Core Objective',
    keyDesc: "PolyAi-Claw's goal is to continuously identify probability mispricing in the market and execute systematic arbitrage — the same principle as quant funds seeking pricing deviations in options markets.",
    keyHighlight: 'continuously identify probability mispricing and execute systematic arbitrage',
  },
  profitMechanism: {
    badge: 'Core Profit Mechanism',
    title1: 'PolyAi-Claw\'s',
    title2: 'Three Profit Mechanisms',
    desc: 'A stable probability arbitrage system through EV edge detection, millisecond execution, and three-tier capital management',
    m1_title: 'EV Edge Detection',
    m1_sub: 'Probability Edge Detection',
    m1_desc: 'Core logic: Market Price ≠ True Probability. When AI detects a deviation between market implied probability and true probability, it auto-enters the EV edge zone and triggers a position.',
    m1_marketImplied: 'Market Implied Probability',
    m1_aiTrue: 'AI True Probability',
    m1_probDiff: 'Probability Edge',
    m1_note: 'Same principle as quant fund options arbitrage',
    m2_title: 'Millisecond Auto Execution',
    m2_sub: 'Millisecond Execution',
    m2_desc: 'Fully automated execution framework ensures arbitrage opportunities are never lost to human error. Brings the trading model closer to algorithmic trading systems.',
    m2_i0_title: 'Millisecond Execution Speed',
    m2_i0_sub: 'Avg <200ms',
    m2_i1_title: 'Fully On-chain Transparent',
    m2_i1_sub: 'Polygon PoS',
    m2_i2_title: 'Zero Human Interference',
    m2_i2_sub: 'Smart Contract Auto Settlement',
    m2_network_label: 'Execution Network',
    m2_network_val: 'Polygon + USDC',
    m3_title: 'Three-Tier Capital Management',
    m3_sub: 'Three-Tier Risk Control',
    m3_desc: 'Avoids high leverage and single-point risk in traditional speculation. Three-layer capital structure ensures long-term stable system operation.',
    m3_i0_title: 'Stable Return Pool',
    m3_i0_desc: 'High-certainty probability arbitrage, lowest risk',
    m3_i1_title: 'Volatility Arbitrage Pool',
    m3_i1_desc: 'Captures market volatility, high return elasticity',
    m3_i2_title: 'Hedge Protection Pool',
    m3_i2_desc: 'Low-correlation event hedging, reduced systemic risk',
    m3_risk: 'Single Event Exposure < 5%',
    formula_title: 'Probability Arbitrage Formula',
    formula_marketPrice: 'Market Price',
    formula_aiTrue: 'AI True Probability',
    formula_trigger: 'Trigger Position',
    formula_arbitrage: 'Arbitrage',
    formula_hiddenPct: 'Implied 60%',
    formula_evEdge: 'EV Edge +12%',
    formula_autoExec: 'Auto Execute',
  },
  fundManagement: {
    riskTitle: 'Core Risk Control Principles',
    riskDesc: 'The system sets a maximum single-event exposure of < 5%, ensuring that even extreme outcomes in a single event will not significantly impact the overall fund pool.',
    riskHighlight: 'max single-event exposure < 5%',
    stat_maxSingle: 'Max Single Investment',
    stat_maxSingleVal: '5%',
    stat_diversify: 'Capital Diversification',
    stat_diversifyVal: '20+ Events',
    stat_stopLoss: 'Stop-loss Mechanism',
    stat_stopLossVal: 'Auto Triggered',
    vizTitle: 'Risk Control Visualization',
    viz_exposure: 'Exposure Control',
    viz_diversify: 'Capital Diversification',
    viz_correlation: 'Event Correlation',
    viz_stability: 'Strategy Stability',
  },
  techAdvantage: {
    badge: 'Core Advantages',
    title1: 'Why PolyAi-Claw',
    title2: 'Beats Traditional Prediction Markets?',
    dim_decision: 'Decision Basis',
    dim_monitor: 'Monitor Frequency',
    dim_execute: 'Execution Method',
    dim_model: 'Cognitive Model',
    dim_risk: 'Risk Control',
    human_decision: 'Emotion-driven',
    human_monitor: 'Low-frequency',
    human_execute: 'Manual Orders',
    human_model: 'Subjective',
    human_risk: 'Experience-based',
    ai_decision: 'Data-driven',
    ai_monitor: 'High-frequency',
    ai_execute: 'Auto Execute',
    ai_model: 'Probability Model',
    ai_risk: 'Algorithm Managed',
    humanPredict: 'Human Prediction',
    adv1_title: 'Data-driven vs Emotion-driven',
    adv1_desc: 'PolyAi-Claw models based on multi-source data including political events, macro economics, social sentiment, and order book data — using Bayesian probability updates to calculate true probabilities in real time.',
    adv1_label: 'Data Sources',
    adv2_title: 'High-frequency vs Low-frequency',
    adv2_desc: 'The system monitors market changes 24/7 without interruption, identifying probability deviation opportunities in real time. Humans cannot sustain continuous high-frequency monitoring.',
    adv2_label: 'Monitor Frequency',
    adv3_title: 'Auto Execute vs Manual Orders',
    adv3_desc: 'Millisecond auto execution via smart contracts on the Polygon chain — no human intervention, no delay or error, ensuring no arbitrage opportunity is missed.',
    adv3_label: 'Execution Speed',
    adv4_title: 'Algorithm Managed vs Experience-based',
    adv4_desc: 'Three-tier capital management system with single-event exposure strictly controlled below 5%. Low-correlation event hedging strategies reduce systemic risk far beyond human judgment.',
    adv4_label: 'Risk Control',
    conclusion_title: 'From Prediction Market to Poly Ai Claw Intelligence Engine',
    conclusion_desc1: 'This upgrades PolyWin from a simple Prediction Market to',
    conclusion_brand: 'Poly Ai Claw Intelligence Engine',
    conclusion_desc2: 'The Poly Ai Claw Quantitative Prediction System',
  },
  productNarrative: {
    badge: 'Product Positioning',
    title: 'The True Identity of PolyWin',
    desc: 'PolyWin is not just a prediction market — it is an integrated system combining AI, prediction markets, OpenClaw, and quantitative trading. The long-term strategy is to build Polywin Market, PolyWin\'s own prediction platform.',
    item0_title: 'Probability Arbitrage System',
    item0_sub: 'Continuously captures market mispricing',
    item1_title: 'Disciplined Execution Model',
    item1_sub: 'Automated, zero emotional interference',
    item2_title: 'AI Risk Management Engine',
    item2_sub: 'Intelligent risk control for safety',
    visual_title: 'Three Visual Expressions',
    vis0_title: '① Probability Curve Deviation Chart',
    vis0_desc: 'Shows AI probability curve vs. market pricing curve, highlighting clear price deviation zones',
    vis0_ai: 'AI Probability',
    vis0_market: 'Market Price',
    vis0_diff: 'Prob. Difference',
    vis1_title: '② Risk Control Radar Chart',
    vis1_desc: 'Displays risk exposure, capital diversification, event correlation, and strategy stability',
    vis1_exposure: 'Risk Exposure',
    vis1_diversify: 'Capital Spread',
    vis1_safe: 'Safe Zone',
    vis1_safeVal: 'Entered',
    vis2_title: '③ AI Auto Trade Flow Diagram',
    vis2_desc: 'Highlights the complete loop: Data → AI → Execute → Settle → Compound',
    vis2_exec: 'Execution Speed',
    vis2_execVal: 'Millisecond',
    vis2_auto: 'Automation Rate',
    vis2_compound: 'Compound Loop',
    vis2_compoundVal: 'Continuous',
    core_title: 'Core Value Proposition',
    core_desc: 'Through automated probability pricing and time compounding, PolyAi-Claw continuously captures pricing deviations in prediction markets while maintaining strict risk control.',
    core_highlight: 'automated probability pricing and time compounding',
    core_item0: 'Automated Probability Pricing',
    core_item1: 'Time Compounding Mechanism',
    core_item2: 'Risk Control Guarantee',
    core_item3: 'Continuous Deviation Capture',
  },
  profitFlywheel: {
    badge: 'AI-driven Financial Growth Flywheel',
    title1: 'PolyWin AI',
    title2: 'Profit Flywheel Model',
    desc: "PolyWin's growth doesn't depend on single-trade profits — it relies on an AI-driven continuous compounding system, forming a self-reinforcing loop",
    descHighlight: 'AI-driven continuous compounding system',
    aiCore: 'AI Probability Engine',
    step1_title: 'Global Data Input',
    step1_sub: 'Global Data',
    step1_desc: 'AI continuously monitors news events, political changes, macro economic data, social sentiment, and prediction market order books',
    step1_tech: '50+ Data Sources',
    step1_techDetail: 'Multi-source real-time fusion',
    step2_title: 'AI Probability Modeling',
    step2_sub: 'Probability Modeling',
    step2_desc: 'Uses Bayesian probability models, sentiment correction models, and market pricing models to calculate true probabilities',
    step2_tech: 'Bayesian Model',
    step2_techDetail: 'Dynamic real-time probability update',
    step3_title: 'Detect Probability Deviation',
    step3_sub: 'Probability Edge',
    step3_desc: 'When AI true probability > market implied probability, identified as EV Positive Opportunity',
    step3_tech: 'EV Deviation >5%',
    step3_techDetail: 'Positive EV auto-detection',
    step4_title: 'Auto Trade Execution',
    step4_sub: 'Algorithmic Execution',
    step4_desc: 'System auto-completes position building, hedging, and order execution on the Polygon chain',
    step4_tech: 'Smart Contract',
    step4_techDetail: '<200ms millisecond execution',
    step5_title: 'Prediction Market Profit',
    step5_sub: 'Market Profit',
    step5_desc: 'When event outcome is confirmed or market probability reverts, the system earns arbitrage returns',
    step5_tech: 'Polygon PoS',
    step5_techDetail: 'On-chain auto settlement',
    step6_title: 'Capital Recovery',
    step6_sub: 'Capital Recycling',
    step6_desc: 'The system auto-returns profitable capital to the fund pool',
    step6_tech: 'USDC Settlement',
    step6_techDetail: 'Stablecoin zero FX loss',
    step7_title: 'Compound Growth',
    step7_sub: 'Compounding Capital',
    step7_desc: 'As the fund pool grows, AI can participate in larger markets, more events, and more arbitrage opportunities',
    step7_tech: 'Auto Compound',
    step7_techDetail: 'Profit auto-reinvestment loop',
    step8_title: 'Larger Capital Scale',
    step8_sub: 'Larger Liquidity Pool',
    step8_desc: 'Larger capital scale means higher trading capacity, more stable returns, and stronger market influence',
    step8_tech: 'Liquidity Expansion',
    step8_techDetail: 'Scale advantage compounds',
    sum0_label: 'Data Layer',
    sum0_tech: '50+ Multi-source Streams',
    sum0_detail: 'Bayesian real-time modeling',
    sum1_label: 'Execution Layer',
    sum1_tech: 'Smart Contract Automation',
    sum1_detail: 'Polygon PoS on-chain settlement',
    sum2_label: 'Settlement Layer',
    sum2_tech: 'USDC Stablecoin',
    sum2_detail: 'Zero FX loss settlement',
    sum3_label: 'Compound Layer',
    sum3_tech: 'Auto Reinvestment',
    sum3_detail: 'Scale expansion positive loop',
    key0_title: 'Self-reinforcing Loop',
    key0_desc: 'Every profit strengthens system capability, forming positive feedback loops for exponential growth',
    key1_title: 'AI-driven Growth',
    key1_desc: "Growth doesn't depend on referrals — it relies on the AI system's own probability arbitrage capability and compounding effect",
    key2_title: 'Continuous Compounding System',
    key2_desc: 'Through automated profit recovery and reinvestment, the fund pool continuously expands for stable return growth',
    formula_title: 'Flywheel Growth Formula',
    f0_label: 'AI Data',
    f1_label: 'Prob. Arbitrage',
    f2_label: 'Profit',
    f3_label: 'Compound',
    f4_label: 'More Capital',
    f5_label: 'More Arbitrage',
    connected: 'Connected · Live',
  },
  sysArch: {
    badge: 'Official Architecture',
    title1: 'PolyAi-Claw AI',
    title2: 'System Architecture',
    desc: 'A complete AI decision system from data collection to auto-settlement, using quant-fund-grade risk control models for transparent, automated, human-free trade execution',
    coreBadge: 'Core AI Brain',
    feat0_title: 'Complete AI System Structure',
    feat0_desc: 'Not a simple strategy — a complete AI decision system covering the full flow from data collection, intelligent analysis, risk control to auto execution',
    feat1_title: 'Quant-Fund-Grade Risk Control',
    feat1_desc: 'Professional quant fund risk control models including exposure control, correlation hedging, and dynamic capital allocation',
    feat2_title: 'Scalable Architecture',
    feat2_desc: 'Future-expandable to multi-market, multi-chain, multi-strategy — supports Azuro, Zeitgeist, Omen, and more prediction markets',
    dataflow_title: 'Data Flow',
  },
  tradingExample: {
    badge: 'Live Case Study',
    title1: 'PolyAi-Claw BTC',
    title2: '5-Minute Arbitrage Strategy',
    desc: 'The system does only three things: Detect abnormal volatility → Last 60-second probability judgment → Trade in the safe zone',
    s1_title: 'BTC Market Data',
    s1_sub: 'Real-time Market Monitoring',
    s1_notice: 'Sample data for strategy trigger conditions',
    s1_price: 'Current Price',
    s1_eg: 'Example',
    s1_priceVol: 'Price Volatility',
    s1_vol: 'Volume',
    s1_depth: 'Order Book Depth',
    s1_trigger: 'Trigger: Volatility ≥ 100 USD',
    s1_met: 'Met',
    s2_title: 'PolyAi-Claw Engine',
    s2_sub: 'AI Probability Calculation',
    s2_marketImplied: 'Market Implied Probability',
    s2_aiTrue: 'AI True Probability',
    s2_yes: 'YES',
    s2_probDiff: 'Probability Edge',
    s2_safe: 'Entering Safe Trade Zone',
    s3_title: 'Auto Trade + Profit',
    s3_sub: 'Auto Execution & Returns',
    s3_exec: 'Trade Execution',
    s3_buyYes: 'Buy YES',
    s3_buyPrice: 'Entry Price',
    s3_settlement: 'Prediction Market Settlement',
    s3_correct: 'Correct Prediction',
    s3_profit: 'Per Trade Return',
    s3_avg: 'Average Return Rate',
    s3_recycle: 'Returns Recycled to Pool',
    chart_title: 'AI Probability vs Market Probability Deviation',
    chart_sub: 'Real-time tracking of Poly Ai Claw engine vs. market pricing probability deviation to identify arbitrage windows',
    chart_live: 'Live Analysis',
    chart_curDev: 'Current Deviation',
    chart_signal: 'Arbitrage Signal',
    chart_signalVal: 'Triggered',
    chart_winRate: 'Win Rate Forecast',
    chart_bottom_implied: 'Market Implied Probability',
    chart_bottom_dev: 'Probability Deviation Zone',
    chart_bottom_dev_sub: 'Arbitrage opportunity triggered',
    chart_bottom_ai: 'AI True Probability',
    chart_bottom_ai_sub: 'Poly Ai Claw Model Output',
    chart_bottom_implied_sub: 'Based on market price',
    chart_marketProb: 'Market Implied Prob.',
    chart_aiProb: 'AI True Probability',
    chart_deviationArea: 'Arbitrage Deviation Zone',
    maxDeviation: 'Max Deviation',
    deviation: 'Deviation',
    flow_title: 'Full Strategy Flow',
    flow0_title: 'BTC Data Monitor',
    flow0_desc: 'Live price/volume',
    flow1_title: 'Volatility Detection',
    flow1_desc: '≥ 100 USD',
    flow2_title: 'Time Filter',
    flow2_desc: 'Last 60 seconds',
    flow3_title: 'Win Rate Assessment',
    flow3_desc: '≥ 60-70%',
    flow4_title: 'Auto Execute',
    flow4_desc: 'Buy YES/NO',
    flow5_title: 'Market Settlement',
    flow5_desc: '1 USDC',
    flow6_title: 'Compound Returns',
    flow6_desc: '3%-10%',
    risk0_title: 'Risk Control Structure',
    risk0_i0: 'Single event capital limit < 5%',
    risk0_i1: 'Multi-event diversification',
    risk0_i2: 'AI dynamic stop-loss',
    risk1_title: 'Timing Strategy',
    risk1_i0: 'Execute in last 60 seconds',
    risk1_i1: 'Avoid emotional trading',
    risk1_i2: 'More complete information',
    risk2_title: 'Historical Performance',
    risk2_i0: 'Historical win rate 80%-90%',
    risk2_i1: 'Average return 3%-10%',
    risk2_i2: 'Stable arbitrage model',
  },
  crossPlatform: {
    badge: 'Cross-Platform Prediction Arbitrage',
    title1: 'PolyAi CLaw',
    title2: 'Cross-Platform Prediction Market Arbitrage Logic',
    desc: 'Bridging Polymarket (Web3) and Kalshi (Fiat) — capturing price deviations with intelligent matching and ultra-fast execution',
    polymarket: 'Polymarket',
    kalshi: 'Kalshi',
    web3: 'Web3',
    fiat: 'Fiat',
    claw: 'CLaw',
    matched: '✓ Matched',
    matching: 'Matching...',
    sec1_badge: '01',
    sec1_accuracy: '99%+ Accuracy',
    sec1_title: 'Intelligent Market Matching',
    sec1_desc: 'Events across both platforms are automatically bound by semantic AI, identifying cross-platform same-underlying price spreads in milliseconds',
    sec2_badge: '02',
    sec2_ms: 'Millisecond Execution',
    sec2_title: 'Unified Integration & Ultra-fast Trading',
    sec2_desc: 'Unified API/SDK connects multiple platforms, with millisecond synchronized order placement ensuring zero-delay arbitrage window capture',
    connected: 'Connected · Live',
    latency: 'Order Latency',
    dailyEvents: 'Daily Matched Events',
    successRate: 'Execution Success Rate',
    platforms: 'Synced Platforms',
    sec3_badge: '03',
    sec3_analytics: 'Live Analytics',
    sec3_title: 'Arbitrage Data Visualization',
    sec3_desc: 'Real-time price deviation, arbitrage returns, and growth trends — dynamically presenting cross-platform arbitrage value',
    priceDeviation: 'Price Deviation Distribution',
    volumeTitle: 'Monthly Volume (K USDC)',
    h1Volume: 'H1 Cumulative Volume (USDC)',
    arbIndicators: 'Arbitrage Advantage Metrics',
    arbSuccess: 'Spread Arbitrage Success Rate',
    semanticAccuracy: 'Semantic Match Accuracy',
    liquidity: 'Cross-platform Liquidity',
    hedgeCoverage: 'Hedge Coverage Rate',
  },
  mathAlgo: {
    badge: 'Precision Advanced Math Algorithms',
    title1: 'Polymarket Arbitrage Is Not',
    title2: 'A Simple Math Problem',
    quote: "While you're still manually doing this addition, the quant system is doing something completely different.",
    quoteHighlight1: 'manually doing this addition',
    quoteHighlight2: 'something completely different',
    ch0_title: "What Is Arbitrage? Why Does Polymarket Have So Much?",
    ch0_tag: 'Market Inefficiency',
    ch1_title: 'Cross-market Arbitrage: Logical Dependencies',
    ch1_tag: 'Combinatorial Math',
    ch2_title: 'Bregman Projection: Computing Optimal Arbitrage Size',
    ch2_tag: 'Convex Optimization',
    pipeline_title: 'Polymarket Arbitrage System — Full Architecture',
    layer0: 'Layer 1: Data Collection',
    layer1: 'Layer 2: Dependency Detection',
    layer2: 'Layer 3: Three-tier Optimization Engine',
    layer3: 'Layer 4: Execution & Position Management',
    annualProfit: 'Annual Total Extracted Profit: $39,688,585',
    venn_title: 'Logical Dependency: P(Win by 5%) ⊆ P(Win)',
    contradiction: 'Contradiction! P(subset) > P(parent) → Arbitrage signal triggered',
    contradiction_sub: '0.55 > 0.48 → Logical contradiction → Arbitrage opportunity',
    combinations: 'Market Combinations',
    brutal: 'Brute-force impossible',
    integerConstraints: 'Integer Programming Constraints',
    replaces: 'Replaces 16,384 enumerations',
    bregman_title: 'KL Divergence vs Euclidean Distance',
    coreData: 'Core Data',
    pipeline4: 'System 4-Layer Pipeline',
    annualReturn: 'Annual Profit ≈ $39.7M',
    whyNotEnough_title: 'Why "Prices Sum to $1" Is Not Enough?',
    singleMarket: 'Single Market — Looks Fine',
    crossMarket: 'Cross-market — Exponential Explosion',
  },
  mathProfit: {
    ch3_title: 'Frank-Wolfe Algorithm: Approaching the Answer Step by Step',
    ch3_tag: 'Iterative Algorithm',
    ch3_chapter: 'Chapter 04',
    execTitle: 'Non-atomic Execution Risk',
    profitStructure: 'Profit Structure (Apr 2024 – Apr 2025)',
    totalProfit: 'Total: $39,688,585',
    top10: 'TOP 10 took 20.5%',
    kellyFormula: 'Modified Kelly Position Formula',
    preTrade: 'Pre-trade 4 Checkpoints',
    gate0: 'Sufficient liquidity?',
    gate1: 'Acceptable slippage?',
    gate2: 'Still profitable after slippage?',
    gate3: 'Profit > $0.05 → Execute',
  },
  safuFund: {
    badge: 'Asset Security Guarantee',
    title: 'Polywin SAFU Fund',
    titleHighlight: 'SAFU Fund',
    desc: 'On-chain locked reserves, transparent and verifiable in real time — your assets, our commitment',
    reserveLabel: 'Total Reserve · USDC · Ethereum Mainnet',
    verified: 'On-chain Verified',
    mainDesc: 'The Polywin team has locked 10 million USDC as a SAFU security reserve fund on the Ethereum mainnet, dedicated to protecting user assets in the event of a hack. All funds are fully transparent and verifiable by anyone via Etherscan in real time.',
    mainDescHighlight: '10 million USDC',
    contractLabel: 'Contract Address',
    etherscanBtn: 'Verify on Etherscan',
    stat0_label: 'Reserve Size',
    stat0_sub: 'USDC',
    stat1_label: 'On-chain Lock Rate',
    stat1_sub: 'On-Chain',
    stat2_label: 'Verifiable',
    stat2_sub: 'Real-time',
    stat3_label: 'Historical Payouts',
    stat3_val: '$0',
    stat3_sub: 'Zero Loss Record',
    f0_title: 'On-chain Locked',
    f0_desc: '100% of funds are locked in Ethereum mainnet smart contracts — the team cannot withdraw or transfer arbitrarily',
    f1_title: 'Fully Transparent',
    f1_desc: 'Anyone can view fund balance and all historical transactions in real time via Etherscan',
    f2_title: 'Fast Compensation',
    f2_desc: 'Compensation process initiated within 72 hours of confirmed security incident — user assets protected first',
    f3_title: 'Independent Audit',
    f3_desc: 'Third-party security agencies regularly audit contract code to ensure reserve funds are genuine and unaltered',
  },
  cta: {
    badge: 'Get Started Now',
    title1: 'Start Using',
    title2: 'Poly Ai Claw',
    title3: 'Intelligence Engine',
    desc: 'No coding experience required — configure AI strategies in 3 minutes',
    btn: 'Launch AI-Claw',
    trust0: 'Polygon Official Partner',
    trust1: 'Audit Agency Certified',
    trust2: '10,000+ Active Users',
    trust3: '$50M+ Cumulative Volume',
  },
  footer: {
    tagline: 'Poly Ai Claw Intelligence Engine',
    product: 'Product',
    resources: 'Resources',
    social: 'Social Media',
    link0: 'Poly AI-Claw Algorithm',
    link1: 'Risk Control System',
    link2: 'Data API',
    link3: 'Developer Docs',
    res0: 'Tutorial',
    res1: 'Strategy Cases',
    res2: 'Whitepaper',
    res3: 'Community Forum',
    copyright: '© 2026 PolyWin. All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
};
